import React, { useState, useEffect } from "react";
import Button from "../../components/controls/Button";
import { useTranslation } from "react-i18next";
import FormInput from "../../components/controls/FormInput";
import DropdownMenu from "../../components/controls/DropdownMenu";

const getCompanyTitle = (selectedCompany, uniqueCompanies, t) => {
  if (!selectedCompany) return t("storeFilters.allCompanies");
  const found = uniqueCompanies.find((c) => `${c.id}` === `${selectedCompany}`);
  return found?.name || t("storeFilters.allCompanies");
};

export default function StoreFilters({
  activeType,
  setActiveType,
  searchTerm,
  handleSearchChange,
  maxPrice,
  minPrice,
  setMinPrice,
  setMaxPrice,
  selectedCompany,
  setSelectedCompany,
  uniqueCompanies = [],
}) {
  const { t } = useTranslation();

  const [tempMinPrice, setTempMinPrice] = useState(minPrice);
  const [tempMaxPrice, setTempMaxPrice] = useState(maxPrice);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setMinPrice(tempMinPrice);
      setMaxPrice(tempMaxPrice);
    }, 400);
    return () => clearTimeout(timeout);
  }, [tempMinPrice, tempMaxPrice, setMinPrice, setMaxPrice]);

  const companyTitle = getCompanyTitle(selectedCompany, uniqueCompanies, t);

  const onSearchChanged = (_, value) =>
    handleSearchChange({ target: { value } });

  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-4 sticky top-24">
      <h2 className="text-lg font-semibold">{t("storeFilters.title")}</h2>

      <div className="flex flex-col space-y-2">
        <label className="font-medium">{t("storeFilters.category")}</label>

        <div className="flex gap-2">
          <Button
            as="button"
            type="button"
            onClick={() => setActiveType("product")}
            color={activeType === "product" ? "mint" : "primary"}
            size="md"
            className="w-full px-4 py-2 font-semibold"
            aria-pressed={activeType === "product"}
            aria-label={t("storeFilters.aria.pressProducts")}
          >
            {t("storeFilters.products")}
          </Button>

          <Button
            as="button"
            type="button"
            onClick={() => setActiveType("service")}
            color={activeType === "service" ? "mint" : "primary"}
            size="md"
            className="w-full px-4 py-2 font-semibold"
            aria-pressed={activeType === "service"}
            aria-label={t("storeFilters.aria.pressServices")}
          >
            {t("storeFilters.services")}
          </Button>
        </div>
      </div>

      <FormInput
        label={t("storeFilters.search")}
        name="search"
        value={searchTerm}
        placeholder={t("storeFilters.searchPlaceholder")}
        onChange={onSearchChanged}
        showRequiredOrOptional={false}
        additionalClass="h-12"
      />

      <div className="space-y-2">
        <label className="font-medium">{t("storeFilters.price")} $</label>
        <div className="flex gap-2">
          <div className="w-full">
            <FormInput
              label=""
              name="minPrice"
              type="number"
              value={`${tempMinPrice ?? ""}`}
              onChange={(_, v) => setTempMinPrice(Number(v))}
              showRequiredOrOptional={false}
              additionalClass="h-12"
            />
          </div>

          <div className="w-full">
            <FormInput
              label=""
              name="maxPrice"
              type="number"
              value={`${tempMaxPrice ?? ""}`}
              onChange={(_, v) => setTempMaxPrice(Number(v))}
              showRequiredOrOptional={false}
              additionalClass="h-12"
            />
          </div>
        </div>
      </div>

      {uniqueCompanies.length > 1 && (
        <div className="space-y-2">
          <label className="font-medium">{t("storeFilters.company")}</label>

          <DropdownMenu
            title={companyTitle}
            isDropdown={true}
            items={[
              {
                label: t("storeFilters.allCompanies"),
                onClick: () => setSelectedCompany(""),
              },
              ...uniqueCompanies.map((company) => ({
                label: company.name,
                onClick: () => setSelectedCompany(`${company.id}`),
              })),
            ]}
          />
        </div>
      )}
    </div>
  );
}
